import type { JSX, ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { NoAccessPage } from "../../app/RequireAccess";
import { Loading } from "../../shared/ui/Loading";
import { usePlatformAccess, usePlatformAccessState } from "./PlatformAccessContext";
import { PlatformAccessUnavailablePage } from "./PlatformAccessUnavailablePage";
import { isPlatformPath, platformPaths } from "./platformNavigation";

/** Properties of {@link PlatformGate}. */
export interface PlatformGateProps {
  /** The application of the tenants, shown to everybody without a platform role. */
  readonly children: ReactNode;
}

/**
 * Chooses the area after the sign-in (F-69). Operator staff (`PlatformAdmin`, `PlatformSupport`)
 * start in the operator area and never see the application of the tenants: no clock, no tenant
 * navigation, no tenant selection. Everybody else gets the application of the tenants, and the
 * operator area sends them to its start page. While the first answer of the server is awaited and
 * nothing is remembered on this device, the gate shows the loading state; when that request failed
 * or cannot run offline, an error with „Erneut versuchen“ instead of the application of the tenants
 * (F-84): only a settled answer without a platform role opens it.
 *
 * @param props - Component properties.
 * @returns The operator area, the application of the tenants, a redirect, the loading state or the
 * error.
 */
export function PlatformGate(props: PlatformGateProps): JSX.Element {
  const state = usePlatformAccessState();
  const { access } = state;
  const inArea = isPlatformPath(useLocation().pathname);
  let element: JSX.Element;

  if (access === "loading") {
    element = <Loading />;
  } else if (access === "unavailable") {
    element = (
      <PlatformAccessUnavailablePage
        error={state.error}
        retrying={state.retrying}
        onRetry={state.retry}
      />
    );
  } else if (access === "none") {
    element = inArea ? <Navigate to="/" replace /> : <>{props.children}</>;
  } else {
    element = inArea ? <Outlet /> : <Navigate to={platformPaths.root} replace />;
  }

  return element;
}

/**
 * Route guard of the pages that only change something, such as the onboarding: operator support
 * (`PlatformSupport`) opening one by its address sees the neutral „Keine Berechtigung“ page instead
 * of a form whose every request would fail. The server stays authoritative.
 *
 * @returns The page or the neutral page.
 */
export function RequirePlatformManage(): JSX.Element {
  const access = usePlatformAccess();
  const element = access === "manage" ? <Outlet /> : <NoAccessPage />;

  return element;
}
